import type { FileData } from "../types";

interface ViewRUploadResult {
  object_name?: string;
  download_url?: string;
  gcs_path?: string;
  display_name?: string;
  created_at?: string;
  updated_at?: string;
  thumbnail_url?: string | null;
}

const stripViewRExtension = (value: string): string =>
  value.replace(/\.viewr$/i, "");

const stripGcsBucket = (value: string): string =>
  value.replace(/^gs:\/\/[^/]+\//, "");

const getNameFromPath = (value?: string): string | null => {
  if (!value) return null;

  const normalized = stripGcsBucket(value).replace(/^\/+|\/+$/g, "");
  if (!normalized) return null;

  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const fileSegment = segments[segments.length - 1];
  if (fileSegment.toLowerCase().endsWith(".viewr")) {
    return stripViewRExtension(fileSegment);
  }

  return segments.length >= 2 ? segments[segments.length - 2] : fileSegment;
};

export const getCanonicalViewRDisplayName = (
  file: File,
  result: ViewRUploadResult
): string => {
  const fileBaseName = stripViewRExtension(file.name) || "Untitled";

  return (
    result.display_name ||
    getNameFromPath(result.object_name) ||
    getNameFromPath(result.gcs_path) ||
    fileBaseName
  );
};

export const buildOptimisticViewRFileFromGcsPath = (
  gcsPath: string,
  options?: {
    title?: string;
    downloadUrl?: string;
    createdAt?: string;
  },
): FileData => {
  const displayName =
    options?.title?.trim() ||
    getNameFromPath(gcsPath) ||
    "Untitled";
  const nowIso = new Date().toISOString();
  const createdAt = options?.createdAt || nowIso;
  const objectPath = stripGcsBucket(gcsPath);

  return {
    name: `${displayName}.viewr`,
    filename: `${displayName}.viewr`,
    title: displayName,
    gcs_path: gcsPath,
    full_path: objectPath || gcsPath,
    download_url: options?.downloadUrl || undefined,
    created_at: createdAt,
    createdAt,
    updated: createdAt,
    updated_at: createdAt,
  };
};

export const buildOptimisticViewRFileData = (
  file: File,
  result: ViewRUploadResult
): FileData => {
  const displayName = getCanonicalViewRDisplayName(file, result);
  const nowIso = new Date().toISOString();
  const gcsPath = result.gcs_path || "";
  const fullPath = result.object_name || stripGcsBucket(gcsPath);

  return {
    name: `${displayName}.viewr`,
    filename: `${displayName}.viewr`,
    title: displayName,
    gcs_path: gcsPath,
    full_path: fullPath,
    download_url: result.download_url || undefined,
    thumbnail_url: result.thumbnail_url || undefined,
    size: file.size,
    created_at: result.created_at || nowIso,
    updated: result.updated_at || result.created_at || nowIso,
    updated_at: result.updated_at || result.created_at || nowIso,
  };
};

export const mergeOptimisticFinalFile = (
  previous: FileData[] | undefined,
  optimisticFile: FileData
): FileData[] => {
  const prev = Array.isArray(previous) ? previous : [];
  const optimisticKey =
    optimisticFile.gcs_path || optimisticFile.full_path || optimisticFile.name;
  const deduped = prev.filter(
    (item) => (item.gcs_path || item.full_path || item.name) !== optimisticKey
  );
  return [optimisticFile, ...deduped];
};
